import { makeAutoObservable, runInAction } from "mobx";
import { surveysService } from "../services";

const buildCategoryBreakdown = (answers = []) => {
  const totals = {};
  answers.forEach((answer) => {
    const category = answer.category || "common";
    if (!totals[category]) {
      totals[category] = { awardedPoints: 0, maxPoints: 0 };
    }
    totals[category].awardedPoints +=
      Number(answer.awardedPoints ?? answer.pointsAwarded) || 0;
    totals[category].maxPoints += Number(answer.maxPoints) || 0;
  });
  return Object.entries(totals).map(([category, stats]) => {
    const percentage =
      stats.maxPoints > 0
        ? Math.round((stats.awardedPoints / stats.maxPoints) * 100)
        : null;
    return {
      category,
      awardedPoints: stats.awardedPoints,
      maxPoints: stats.maxPoints,
      percentage,
    };
  });
};

class SurveyResponseDetailStore {
  detail = null;
  loading = false;
  error = "";

  constructor() {
    makeAutoObservable(this);
  }

  get answers() {
    return this.detail?.answers || [];
  }

  get survey() {
    return this.detail?.survey || {};
  }

  get response() {
    return this.detail?.response || {};
  }

  get summary() {
    return this.detail?.stats || {};
  }

  get respondentCompany() {
    const response = this.response;
    return (
      response?.respondentCompany ||
      this.detail?.respondentCompany ||
      (response?.respondent?.companyName
        ? { companyName: response.respondent.companyName }
        : null)
    );
  }

  get derivedStats() {
    const response = this.response;
    const summary = this.summary;
    const answers = this.answers;
    const totalScore = Number(response?.totalScore) || 0;
    const maxPossibleScore =
      Number(summary.maxPossibleScore) ||
      answers.reduce((sum, answer) => sum + (Number(answer.maxPoints) || 0), 0);
    const scorePercentage =
      maxPossibleScore > 0
        ? Math.round((totalScore / maxPossibleScore) * 100)
        : null;
    const categoryBreakdown =
      summary?.categoryBreakdown?.length > 0
        ? summary.categoryBreakdown
        : buildCategoryBreakdown(answers);

    return {
      totalScore,
      maxPossibleScore,
      scorePercentage,
      answeredQuestions: summary.answeredQuestions ?? answers.length,
      totalQuestions:
        summary.totalQuestions ??
        this.survey?.questions?.length ??
        answers.length,
      categoryBreakdown,
    };
  }

  get groupedAnswers() {
    return this.answers.reduce((acc, answer) => {
      const key = answer.category || "common";
      if (!acc[key]) acc[key] = [];
      acc[key].push(answer);
      return acc;
    }, {});
  }

  clear = () => {
    this.detail = null;
    this.loading = false;
    this.error = "";
  };

  load = async (responseId, t) => {
    if (!responseId) return;
    runInAction(() => {
      this.loading = true;
      this.error = "";
    });

    try {
      const data = await surveysService.getResponseDetail(responseId);
      const payload = data?.data || data;
      runInAction(() => {
        this.detail = payload || null;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error?.message ||
          (t ? t("Unable to load response.") : "Unable to load response.");
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };
}

export const surveyResponseDetailStore = new SurveyResponseDetailStore();
